import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc, getDocs, collection, query, where, Timestamp } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import { SYSTEM_ADMIN, generateKioskToken, getDeviceId } from "../utils/constants";
import {
  isMfaRequired, resolverOf, startChallenge, finishChallenge,
  makeRecaptcha, mfaErrorMessage,
} from "../utils/mfa";

function Login() {
  const _params = new URLSearchParams(window.location.search);
  const [mode, setMode] = useState(_params.get("register") === "1" ? "register" : "login"); // login | register | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [companyCode, setCompanyCode] = useState(_params.get("code") || "");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // 2段階認証。パスワードが通ったあと、確認コードの入力に切り替える。
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const loginVerifierRef = useRef(null);
  const [kioskToken, setKioskToken] = useState(generateKioskToken);
  const [isApprovedDevice, setIsApprovedDevice] = useState(false);
  const [kioskCompanyId, setKioskCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");

  useEffect(() => {
    const approved = localStorage.getItem("miyabi_device_approved");
    const cid = localStorage.getItem("miyabi_device_companyId");
    const cname = localStorage.getItem("miyabi_company_name");
    const clogo = localStorage.getItem("miyabi_company_logo");
    if (approved === "true") {
      setIsApprovedDevice(true);
      setKioskCompanyId(cid || null);
      setCompanyName(cname || "");
      setCompanyLogo(clogo || "");
      // 登録した経路によっては会社名が保存されていないので、会社の情報を読み直す
      if (cid) {
        getDoc(doc(db, "companies", cid)).then((snap) => {
          if (!snap.exists()) return;
          const data = snap.data();
          setCompanyName(data.name || "");
          setCompanyLogo(data.logo || "");
          localStorage.setItem("miyabi_company_name", data.name || "");
          localStorage.setItem("miyabi_company_logo", data.logo || "");
        }).catch(() => { /* 読めなければ保存済みの表示のまま */ });
      }
    }
  }, []);

  useEffect(() => {
    if (!isApprovedDevice) return;
    const timer = setInterval(() => setKioskToken(generateKioskToken()), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [isApprovedDevice]);

  const appUrl = "https://miyabicompany-manager.web.app";
  const kioskUrl = `${appUrl}?kiosk=${kioskToken}${kioskCompanyId ? `&cid=${kioskCompanyId}` : ""}`;

  // reCAPTCHAは使い回すと二重登録で例外になるので、送信のたびに作り直す
  const freshLoginVerifier = () => {
    try { loginVerifierRef.current?.clear(); } catch { /* 片付けの失敗は無視 */ }
    loginVerifierRef.current = null;
    // clear() だけでは要素の中身が残り、作り直すと失敗するので自分で空にする
    const el = document.getElementById("login-recaptcha");
    if (el) el.innerHTML = "";
    loginVerifierRef.current = makeRecaptcha("login-recaptcha");
    return loginVerifierRef.current;
  };

  const sendMfaCode = async (resolver) => {
    setMfaError("");
    try {
      setMfaVerificationId(await startChallenge(resolver, freshLoginVerifier()));
    } catch (err) {
      console.error("確認コードの送信に失敗しました:", err);
      setMfaError(mfaErrorMessage(err));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      // パスワードは合っていて、2段階認証だけが残っている状態
      if (isMfaRequired(err)) {
        const resolver = resolverOf(err);
        setMfaResolver(resolver);
        setMfaCode("");
        setMode("mfa");
        await sendMfaCode(resolver);
        return;
      }
      setError("メールアドレスまたはパスワードが間違っています");
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    const digits = mfaCode.replace(/\D/g, "");
    if (digits.length !== 6) { setMfaError("6桁の確認コードを入力してください"); return; }
    setMfaBusy(true); setMfaError("");
    try {
      // 成功すると認証状態が変わり、App側で画面が切り替わる
      await finishChallenge(mfaResolver, mfaVerificationId, digits);
    } catch (err) {
      setMfaError(mfaErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  };


  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("パスワードは6文字以上にしてください"); return; }

    const isSystemAdmin = email === SYSTEM_ADMIN;

    // システム管理者以外は会社コード必須
    if (!isSystemAdmin && !companyCode.trim()) { setError("会社コードを入力してください"); return; }

    try {
      // システム管理者以外は会社コードを確認
      if (!isSystemAdmin) {
        const companySnap = await getDoc(doc(db, "companies", companyCode.trim()));
        if (!companySnap.exists()) {
          setError("会社コードが正しくありません");
          return;
        }
      }

      const cid = isSystemAdmin ? "" : companyCode.trim();

      // この会社に既存ユーザーがいるか確認 → いなければ最初の登録者 = 管理者
      let isFirstUser = false;
      if (cid) {
        const existing = await getDocs(query(collection(db, "users"), where("companyId", "==", cid)));
        isFirstUser = existing.empty;
      }

      const cred = await createUserWithEmailAndPassword(auth, email, password);

      const adminPermissions = {
        postNotice: true,
        approveDocument: true,
        distributePayslip: true,
        manageUsers: true,
        manageDevices: true,
        viewAllAttendance: true,
        companyAdmin: true,
      };
      const normalPermissions = {
        postNotice: false,
        approveDocument: false,
        distributePayslip: false,
        manageUsers: false,
        manageDevices: false,
        viewAllAttendance: false,
        companyAdmin: false,
      };

      await setDoc(doc(db, "users", cred.user.uid), {
        uid: cred.user.uid,
        email,
        name,
        phone,
        companyId: cid,
        // 最初の登録者は自動承認＋管理者権限
        status: isFirstUser ? "approved" : "pending",
        permissions: isFirstUser ? adminPermissions : normalPermissions,
        createdAt: Timestamp.now(),
      });
      await auth.signOut();
      setDone(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("このメールアドレスはすでに登録されています");
      } else {
        setError("登録に失敗しました: " + err.message);
      }
    }
  };

  if (done) {
    return (
      <div style={st.container}>
        <div style={st.box}>
          <div style={{ fontSize: "48px", textAlign: "center", marginBottom: "16px" }}>✅</div>
          <h2 style={{ ...st.title, marginBottom: "12px" }}>登録完了</h2>
          <p style={{ textAlign: "center", color: "#7f8c8d", lineHeight: "1.7" }} id="done-msg">
            登録申請が完了しました。<br />管理者の承認後にログインできます。<br />（最初の登録者は自動的に管理者になります）
          </p>
          <button style={{ ...st.button, marginTop: "24px" }} onClick={() => { setMode("login"); setDone(false); setEmail(""); setPassword(""); setName(""); setCompanyCode(""); }}>
            ログイン画面に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={st.container}>
      {isApprovedDevice && (
        <div style={st.qrBox}>
          {/* 会社ロゴ（会社名と会社コードはQRの下に出す） */}
          <div style={st.companyInfo}>
            {companyLogo
              ? <img src={companyLogo} alt="ロゴ" style={st.companyLogo} />
              : <div style={st.companyLogoPlaceholder}>🏢</div>
            }
          </div>
          <div style={st.qrWithCompany}>
            <QRCodeSVG value={kioskUrl} size={110} />
            {companyName && <div style={st.qrCompanyName}>{companyName}</div>}
            {kioskCompanyId && <div style={st.qrCompanyCode}>会社コード<br />{kioskCompanyId}</div>}
          </div>
          <div style={st.qrText}>
            <div style={st.qrTitle}>📱 打刻はこちら</div>
            <div style={st.qrNote}>スマホでQRを読み取ってください</div>
            <div style={st.qrExpire}>※5分ごとに自動更新</div>
          </div>
        </div>
      )}

      <div style={st.box}>
        <h2 style={st.title}>miyabi Company</h2>
        <p style={st.subtitle}>
          {mode === "login" ? "ログイン" : mode === "register" ? "新規登録"
            : "2段階認証"}
        </p>

        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <input style={st.input} type="email" placeholder="メールアドレス" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} required />
            <input style={st.input} type="password" placeholder="パスワード" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required />
            {error && <p style={st.error}>{error}</p>}
            <button style={st.button} type="submit">ログイン</button>
            <div style={{ marginTop: "16px", borderTop: "1px solid #eee", paddingTop: "16px" }}>
              <p style={{ textAlign: "center", fontSize: "13px", color: "#7f8c8d", marginBottom: "10px" }}>アカウントをお持ちでない方</p>
              <a href="https://miyabicompany-manager.web.app/?signup=1" style={{ display: "block", textAlign: "center", padding: "10px", marginBottom: "8px", border: "1px solid #2c3e50", borderRadius: "8px", color: "#2c3e50", fontSize: "14px", textDecoration: "none", fontWeight: "bold" }}>
                🏢 会社の新規登録はこちら
              </a>
              <a href="https://miyabicompany-manager.web.app/?signup=1&type=employee" style={{ display: "block", textAlign: "center", padding: "10px", border: "1px solid #27ae60", borderRadius: "8px", color: "#27ae60", fontSize: "14px", textDecoration: "none", fontWeight: "bold" }}>
                👤 従業員の新規登録はこちら
              </a>
            </div>
            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <a href="/manual.pdf" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#2980b9", textDecoration: "none" }}>
                📖 操作マニュアルを見る
              </a>
            </div>
          </form>
        )}


        {mode === "mfa" && (
          <form onSubmit={handleMfaSubmit}>
            <p style={{ textAlign: "center", color: "#7f8c8d", fontSize: "13px", lineHeight: "1.8", marginBottom: "20px" }}>
              登録された携帯電話に確認コードを送りました。<br />届いた6桁の数字を入力してください。
            </p>
            <input style={{ ...st.input, letterSpacing: "8px", textAlign: "center", fontSize: "24px" }}
              type="tel" inputMode="numeric" maxLength={6} placeholder="000000" autoFocus
              value={mfaCode} onChange={e => setMfaCode(e.target.value)} />
            {mfaError && <p style={st.error}>{mfaError}</p>}
            <button style={st.button} type="submit" disabled={mfaBusy}>
              {mfaBusy ? "確認中..." : "ログイン"}
            </button>
            <p style={{ textAlign: "center", marginTop: "16px" }}>
              <span style={st.devLink} onClick={() => mfaResolver && sendMfaCode(mfaResolver)}>
                確認コードを再送する
              </span>
            </p>
            <p style={{ textAlign: "center", marginTop: "10px" }}>
              <span style={st.devLink} onClick={() => { setMode("login"); setMfaCode(""); setMfaError(""); }}>
                戻る
              </span>
            </p>
          </form>
        )}

        {/* 不可視のreCAPTCHA。SMSの悪用防止のためFirebaseが必須としている */}
        <div id="login-recaptcha" />

      </div>
    </div>
  );
}

const st = {
  container: { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8", padding: "20px" },
  box: { backgroundColor: "#fff", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "360px" },
  title: { textAlign: "center", color: "#2c3e50", marginBottom: "4px" },
  subtitle: { textAlign: "center", color: "#7f8c8d", marginBottom: "24px" },
  input: { width: "100%", padding: "12px", marginBottom: "12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", boxSizing: "border-box" },
  button: { width: "100%", padding: "12px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", fontSize: "16px", cursor: "pointer" },
  error: { color: "red", fontSize: "14px", marginBottom: "8px" },
  switchText: { textAlign: "center", marginTop: "16px", fontSize: "14px", color: "#7f8c8d" },
  link: { color: "#2980b9", cursor: "pointer", marginLeft: "4px", textDecoration: "underline" },
  devLink: { color: "#bbb", cursor: "pointer", fontSize: "12px", textDecoration: "underline" },
  qrBox: { display: "flex", alignItems: "center", gap: "20px", backgroundColor: "#fff", padding: "18px 24px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", marginBottom: "20px", width: "360px", flexWrap: "wrap" },
  companyInfo: { display: "flex", alignItems: "center", gap: "10px", width: "100%", borderBottom: "1px solid #eee", paddingBottom: "12px", marginBottom: "4px" },
  companyLogo: { width: "40px", height: "40px", objectFit: "contain", borderRadius: "6px" },
  companyLogoPlaceholder: { width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", backgroundColor: "#f0f4f8", borderRadius: "6px" },
  companyName: { fontSize: "15px", fontWeight: "bold", color: "#2c3e50" },
  qrWithCompany: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", maxWidth: "140px" },
  qrCompanyName: { fontSize: "15px", fontWeight: "bold", color: "#2c3e50", textAlign: "center", wordBreak: "break-all" },
  qrCompanyCode: { fontSize: "13px", color: "#2c3e50", textAlign: "center", wordBreak: "break-all" },
  qrText: { display: "flex", flexDirection: "column", gap: "6px" },
  qrTitle: { fontSize: "16px", fontWeight: "bold", color: "#2c3e50" },
  qrNote: { fontSize: "13px", color: "#7f8c8d" },
  qrExpire: { fontSize: "12px", color: "#e74c3c" },
};

export default Login;
